/**
 * Completion Step - Final step showing success
 */

import { formatBillingDate } from '@/renderer/components/billing';
import { BillingCycle } from '@/renderer/types/billing';
import React from 'react';
import { Button } from '../common';

interface CompletionStepProps {
  businessName: string;
  billingCycle?: BillingCycle;
  onFinish: () => void;
  packageName?: string;
  trialEndsAt?: string | null;
}

export const CompletionStep: React.FC<CompletionStepProps> = ({
  businessName,
  billingCycle = 'monthly',
  onFinish,
  packageName,
  trialEndsAt,
}) => {
  return (
    <div className="w-full max-w-md mx-auto text-center">
      <div className="mb-8">
        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-12 h-12 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h2 className="text-3xl font-bold text-pos-dark mb-2">
          Welcome aboard!
        </h2>
        <p className="text-pos-secondary text-lg">
          Your SmartPOS account for{' '}
          <span className="font-semibold text-pos-primary">{businessName}</span>{' '}
          is ready
        </p>
      </div>

      <div className="bg-pos-dark bg-opacity-5 rounded-lg p-6 mb-8 text-left">
        <h3 className="font-semibold text-pos-dark mb-4">What&apos;s Next?</h3>
        <div className="space-y-3">
          <div className="flex items-start">
            <div className="w-6 h-6 bg-pos-primary text-white rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 mr-3 text-sm">
              1
            </div>
            <div>
              <p className="font-medium text-pos-dark">Setup Your Menu</p>
              <p className="text-sm text-pos-secondary">
                Use AI to extract menu from documents or add items manually
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <div className="w-6 h-6 bg-pos-primary text-white rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 mr-3 text-sm">
              2
            </div>
            <div>
              <p className="font-medium text-pos-dark">Configure Printers</p>
              <p className="text-sm text-pos-secondary">
                Connect your receipt and kitchen printers
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <div className="w-6 h-6 bg-pos-primary text-white rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 mr-3 text-sm">
              3
            </div>
            <div>
              <p className="font-medium text-pos-dark">Add Team Members</p>
              <p className="text-sm text-pos-secondary">
                Invite staff and assign roles
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <div className="w-6 h-6 bg-pos-primary text-white rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 mr-3 text-sm">
              4
            </div>
            <div>
              <p className="font-medium text-pos-dark">Start Taking Orders</p>
              <p className="text-sm text-pos-secondary">
                You&apos;re all set to begin processing orders!
              </p>
            </div>
          </div>
        </div>
      </div>

      <Button onClick={onFinish} fullWidth size="lg">
        Go to Dashboard
      </Button>

      <p className="text-sm text-pos-secondary mt-6">
        Your {packageName || 'selected'} plan is active on{' '}
        <span className="font-semibold">
          {billingCycle === 'annual' ? 'annual' : 'monthly'}
        </span>{' '}
        billing. No charges until{' '}
        <span className="font-semibold">
          {trialEndsAt
            ? formatBillingDate(trialEndsAt)
            : 'your 14-day trial ends'}
        </span>
      </p>
    </div>
  );
};
